// Shared public ledger wording. It says "public ledger" and "nodeStream credits", never the ledger's vendor.

// The first director's set-up stopped part way (plan decision D3). Everything made so far is kept, and trying again creates
// only what is missing.
export const SETUP_INCOMPLETE_COPY =
  "We couldn't finish setting up your public ledger accounts. nodeStream has been notified; please try again shortly.";

// The per-company signatories team is kept in step with the signature rule by the server, so it isn't edited by hand
export const SIGNATORIES_TEAM_NOTE = 'Changes through the signatory workflow';

// Another attempt at the same accounts is under way, or one that stopped recently still holds them for a short while
export const SETUP_BUSY_COPY =
  'Your public ledger account is still being set up. Please try again in a couple of minutes.';

// The call was cut off before it answered (it ran out of time, or the connection dropped). What it made is kept, and
// trying again finishes the rest.
export const SETUP_INTERRUPTED_COPY =
  "We couldn't finish creating your public ledger account. Please try again in a couple of minutes.";

// A first director's set-up made their account with a key kept only in the browser it started in, so it can't be finished
// from another browser, or after this browser's site data was cleared
export const SETUP_KEY_ELSEWHERE_COPY =
  "This browser doesn't hold the key your public ledger set-up was started with. Please try again from the browser you started it in.";

// The browser refused to sign: what it was served to sign didn't match what the card shows (#24). Nothing was signed, and
// the PUBLIC_LEDGER team was sent a card; the second wording is for when that report couldn't be sent
export const SIGN_MISMATCH_COPY =
  "This signature couldn't be checked against what's shown, so nothing was signed. The public ledger team has been told.";
export const SIGN_MISMATCH_UNREPORTED_COPY =
  "This signature couldn't be checked against what's shown, so nothing was signed. Please let nodeStream support know.";

// The browser couldn't read what it needs to check a transaction before signing it, so it signed nothing
export const SIGN_CHECK_UNAVAILABLE_COPY =
  "We couldn't check this transaction before signing it, so nothing was signed. Please try again in a moment.";

// The browser couldn't read the schedule from the public ledger's mirror to check it (#24 phase 2b), so it signed nothing
export const MIRROR_UNAVAILABLE_COPY =
  "We couldn't check this against the public ledger. Please try again in a moment.";

// The schedule is no longer open on the public ledger (it ran, was cancelled or expired), so there was nothing to sign
export const SCHEDULE_CLOSED_COPY =
  'This transaction has already been completed or cancelled on the public ledger, so there is nothing left to sign.';

// The coded errors public-ledger/create-account answers with
export const SETUP_INCOMPLETE_CODE = 'PUBLIC_LEDGER_SETUP_INCOMPLETE';
export const SETUP_BUSY_CODE = 'PUBLIC_LEDGER_SETUP_BUSY';
export const SETUP_KEY_ELSEWHERE_CODE = 'PUBLIC_LEDGER_SETUP_KEY_ELSEWHERE';

/**
 * What the create-account card shows for a refused or failed account creation: our own wording for the codes that have it,
 * otherwise the lambda's message (null when there is none). `interrupted` is a call cut off before it answered (see
 * isInterruptedLambdaCall), which may have made some of the accounts. `retry` is true when the card offers "Try again".
 */
export function createAccountFailureCopy(
  code: string | null,
  message: string | null,
  interrupted = false,
): { text: string | null; retry: boolean } {
  if (code === SETUP_INCOMPLETE_CODE) return { text: SETUP_INCOMPLETE_COPY, retry: true };
  if (code === SETUP_BUSY_CODE) return { text: SETUP_BUSY_COPY, retry: true };
  if (code === SETUP_KEY_ELSEWHERE_CODE) return { text: SETUP_KEY_ELSEWHERE_COPY, retry: false };
  if (!code && interrupted) return { text: SETUP_INTERRUPTED_COPY, retry: true };
  return { text: message || null, retry: false };
}

// The coded refusals of the signature gate (#24 phase 3): this device was signed out (or its record is missing or isn't the
// signer's), or the proof that the signature comes from this device didn't check out
export const DEVICE_SIGNED_OUT_CODE = 'DEVICE_SIGNED_OUT';
export const DEVICE_PROOF_INVALID_CODE = 'DEVICE_PROOF_INVALID';

export const DEVICE_SIGNED_OUT_COPY =
  'This device was signed out of your public ledger account, so nothing was signed. Enter your recovery phrase to add it again.';
export const DEVICE_PROOF_INVALID_COPY =
  "We couldn't confirm this signature came from this device, so nothing was signed. Please try again.";

// A signature was refused as signed out and the device check that followed couldn't reach nodeStream (or was overtaken),
// so the key is kept but nothing is signed on this device until a device check answers
export const DEVICE_UNCONFIRMED_COPY =
  "We couldn't confirm this device with nodeStream, so nothing was signed. Please try again in a moment.";
// A signature refused as signed out, but the check that followed found this device still signed in (its record had just
// been replaced, for example), so the key was kept: nothing was signed, and trying again goes through
export const DEVICE_RECHECKED_COPY = "This device's sign-in was just updated, so nothing was signed. Please try again.";

/**
 * What a signature refused by the device gate shows, and whether the device is checked again (signed out only): a tab
 * still holding the id of a row since replaced finds the new one and keeps the public ledger key; the key is forgotten
 * only when that check answers signed out (one that can't answer shows DEVICE_UNCONFIRMED_COPY instead). Null for any
 * other error.
 */
export function deviceRefusal(code: string | null): { text: string; recheck: boolean } | null {
  if (code === DEVICE_SIGNED_OUT_CODE) return { text: DEVICE_SIGNED_OUT_COPY, recheck: true };
  if (code === DEVICE_PROOF_INVALID_CODE) return { text: DEVICE_PROOF_INVALID_COPY, recheck: false };
  return null;
}
